import type { AxiosResponse } from 'axios'

import type { AdReviewV2Api } from './types'

import { defineApi } from '#/api/define-api'
import {
  EXPORT_TASK_BIZ_CODE,
  exportTaskApi,
} from '#/api/modules/common/export-task'
import { requestClient } from '#/api/request'

export const adReviewV2Api = defineApi('operation', ({ prefix }) => {
  const DEFAULT_DW_QUERY_PAGE_TOTAL = 20

  /** 数仓接口可能返回完整 body，也可能被请求客户端提前解包；统一在 API 层归一成数组。 */
  const normalizeDwQueryList = <T>(response: T[] | { data?: T[] }): T[] => {
    if (Array.isArray(response)) return response

    return Array.isArray(response?.data) ? response.data : []
  }

  /** count 数仓接口只返回总数行；缺失 total_count 时临时固定为 20，避免分页组件失去基础总数。 */
  const normalizeDwQueryCount = (
    response:
      | AdReviewV2Api.DwQueryCountBodyResponse
      | AdReviewV2Api.DwQueryCountItem[],
  ) => {
    const countItems = normalizeDwQueryList(response)
    /**
     * 分页总数来自独立 *_count 接口的首行 total_count。
     * 后端返回 0 表示确实没有数据，不能用默认值覆盖。
     */
    const rawTotalCount = (
      countItems?.[0] as AdReviewV2Api.DwQueryCountItem | undefined
    )?.total_count
    const normalizedRawTotalCount =
      typeof rawTotalCount === 'string' ? rawTotalCount.trim() : rawTotalCount
    const totalCount =
      normalizedRawTotalCount === null ||
      normalizedRawTotalCount === undefined ||
      normalizedRawTotalCount === ''
        ? undefined
        : Number(normalizedRawTotalCount)

    return Number.isFinite(totalCount)
      ? totalCount
      : DEFAULT_DW_QUERY_PAGE_TOTAL
  }

  /** 带 page_number/page_size 的数仓接口统一归一为分页结构，列表和总数分别来自两个接口。 */
  const normalizeDwQueryPage = <T>(
    listResponse: T[] | { data?: T[] },
    countResponse:
      | AdReviewV2Api.DwQueryCountBodyResponse
      | AdReviewV2Api.DwQueryCountItem[],
  ): AdReviewV2Api.DwQueryPageResult<T> => {
    return {
      items: normalizeDwQueryList(listResponse),
      total: normalizeDwQueryCount(countResponse),
    }
  }

  const requestDwQueryPage = async <P, T>(
    params: P,
    listPath: string,
    countPath: string,
  ): Promise<AdReviewV2Api.DwQueryPageResult<T>> => {
    // 列表和总数是同一组筛选条件下的完整分页结果，必须等两个接口都成功后再交给页面展示。
    const [listResponse, countResponse] = await Promise.all([
      requestClient.post<AdReviewV2Api.DwQueryPageBodyResponse<T> | T[]>(
        prefix(listPath),
        params,
      ),
      requestClient.post<
        | AdReviewV2Api.DwQueryCountBodyResponse
        | AdReviewV2Api.DwQueryCountItem[]
      >(prefix(countPath), params),
    ])

    return normalizeDwQueryPage(listResponse, countResponse)
  }

  /**
   * 提交广告审核列表导出任务。
   * POST /operation/export/submit
   */
  function exportAdAuditList(
    params: Pick<
      AdReviewV2Api.AdAuditListParams,
      'condition' | 'orderBy' | 'sortBy'
    >,
  ) {
    return exportTaskApi.submitExportTask(
      EXPORT_TASK_BIZ_CODE.ADS_GOVERNOR_V2,
      params,
    )
  }

  /**
   * 查询当前用户锁定中的广告审核单。
   */
  function getLockedAdAuditList(
    params: AdReviewV2Api.AdAuditListParams,
    options?: { signal?: AbortSignal },
  ) {
    return requestClient.post<AdReviewV2Api.AdAuditListResponse>(
      prefix('adsGovernorV2/queryLocked'),
      params,
      options,
    )
  }

  /**
   * 查询未锁定广告审核单。
   */
  function getUnlockedAdAuditList(
    params: AdReviewV2Api.AdAuditListParams,
    options?: { signal?: AbortSignal },
  ) {
    return requestClient.post<AdReviewV2Api.AdAuditListResponse>(
      prefix('adsGovernorV2/query'),
      params,
      options,
    )
  }

  /** 查询单个广告审核单详情。
   * POST /adsGovernorV2/detail
   */
  async function getAdAuditDetail(
    params: AdReviewV2Api.AdAuditDetailParams,
    options?: { signal?: AbortSignal },
  ) {
    const response = await requestClient.post<AdReviewV2Api.AdAuditDetail>(
      prefix('adsGovernorV2/detail'),
      params,
      options,
    )

    // 账户状态相关字段同时兼容 camelCase / snake_case，页面只消费 camelCase。
    if (!response || typeof response !== 'object') {
      return response
    }

    const record = response as AdReviewV2Api.AdAuditDetail &
      Record<string, unknown>
    const readScalar = (camelKey: string, snakeKey: string) => {
      const value = record[camelKey] ?? record[snakeKey]
      return typeof value === 'number' || typeof value === 'string'
        ? value
        : undefined
    }
    const readString = (camelKey: string, snakeKey: string) => {
      const value = record[camelKey] ?? record[snakeKey]
      return typeof value === 'string' ? value : undefined
    }

    return {
      ...response,
      accountStatus: readScalar('accountStatus', 'account_status'),
      mediaAccountStatusLabel: readString(
        'mediaAccountStatusLabel',
        'media_account_status_label',
      ),
      disableReason: readScalar('disableReason', 'disable_reason'),
      disableReasonLabel: readString(
        'disableReasonLabel',
        'disable_reason_label',
      ),
    }
  }

  /** 手动发起整改建议生成。
   * POST /adsGovernorV2/remediationSuggestion/start
   */
  function startRemediationSuggestion(
    params: AdReviewV2Api.RemediationSuggestionParams,
  ) {
    return requestClient.post<AdReviewV2Api.RemediationSuggestionStartResponse>(
      prefix('adsGovernorV2/remediationSuggestion/start'),
      params,
    )
  }

  /** 手动重试机审超时的广告审核单。
   * POST /adsGovernorV2/retryTimedOut/start
   */
  function startRetryTimedOut(params: AdReviewV2Api.RetryTimedOutParams) {
    return requestClient.post<unknown>(
      prefix('adsGovernorV2/retryTimedOut/start'),
      params,
    )
  }

  /** 轮询整改建议生成结果。
   * POST /adsGovernorV2/remediationSuggestion/poll
   */
  function pollRemediationSuggestion(
    params: AdReviewV2Api.RemediationSuggestionPollParams,
  ) {
    return requestClient.post<AdReviewV2Api.RemediationSuggestionPollResponse>(
      prefix('adsGovernorV2/remediationSuggestion/poll'),
      params,
    )
  }

  /** 提交素材人工审核结果。 */
  function approveMaterial(params: AdReviewV2Api.ApproveMaterialParams) {
    return requestClient.post<null>(
      prefix('adsGovernorV2/approveMaterial'),
      params,
    )
  }

  /**
   * 尝试锁定广告审核单，提交素材审核前调用，避免多人同时审核同一广告。
   *
   * 无审核权限由业务 code=403 表达，保留原始响应交给审核表单就地提示，
   * 避免通用请求拦截器先弹出与当前操作脱节的 toast。
   */
  async function tryLockAdAudit(
    params: AdReviewV2Api.AdAuditLockParams,
  ): Promise<AdReviewV2Api.AdAuditLockResult> {
    const response = await requestClient.post<
      AxiosResponse<AdReviewV2Api.AdAuditLockRawResponse>
    >(prefix('adsGovernorV2/tryLock'), params, {
      responseReturn: 'raw',
    })
    const responseData = response.data
    const isSuccess = responseData.code === 0 || responseData.code === 200

    return isSuccess
      ? (responseData.data ?? false)
      : { code: responseData.code }
  }

  /** 解锁广告审核单，当前广告全部素材审核完成后释放锁定。 */
  function unlockAdAudit(params: AdReviewV2Api.AdAuditLockParams) {
    return requestClient.post<AdReviewV2Api.AdAuditLockResult>(
      prefix('adsGovernorV2/unlock'),
      params,
    )
  }

  /**
   * 查询广告成效数据。
   *
   * 详情页成效 Tab 仅先接入请求链路，返回字段待接口稳定后再在页面消费。
   */
  function getAdInsight(
    params: AdReviewV2Api.AdInsightParams,
    options?: { signal?: AbortSignal },
  ) {
    return requestClient.post<AdReviewV2Api.AdInsight>(
      prefix('adsGovernorV2/adInsight'),
      params,
      options,
    )
  }

  /** 查询广告主管理统计列表；该接口带分页参数，统一返回 items + total。 */
  async function getAdManagerCompanyStats(
    params: AdReviewV2Api.AdManagerCompanyStatsParams,
  ) {
    return requestDwQueryPage<
      AdReviewV2Api.AdManagerCompanyStatsParams,
      AdReviewV2Api.AdManagerCompanyStatsItem
    >(
      params,
      'adsGovernorV2/dwQuery/bestads_ad_audit_company_stat',
      'adsGovernorV2/dwQuery/bestads_ad_audit_company_stat_count',
    )
  }

  /** 查询投放层级「仅账户」统计；该接口带分页参数，统一返回 items + total。 */
  async function getAdManagerAccountOnlyStats(
    params: AdReviewV2Api.AdManagerHierarchyStatsParams,
  ) {
    return requestDwQueryPage<
      AdReviewV2Api.AdManagerHierarchyStatsParams,
      AdReviewV2Api.AdManagerHierarchyStatsItem
    >(
      params,
      'adsGovernorV2/dwQuery/bestads_ad_audit_account_only_stat',
      'adsGovernorV2/dwQuery/bestads_ad_audit_account_only_stat_count',
    )
  }

  /** 查询投放层级 Campaign 统计；该接口带分页参数，统一返回 items + total。 */
  async function getAdManagerCampaignOnlyStats(
    params: AdReviewV2Api.AdManagerHierarchyStatsParams,
  ) {
    return requestDwQueryPage<
      AdReviewV2Api.AdManagerHierarchyStatsParams,
      AdReviewV2Api.AdManagerHierarchyStatsItem
    >(
      params,
      'adsGovernorV2/dwQuery/bestads_ad_audit_campaign_only_stat',
      'adsGovernorV2/dwQuery/bestads_ad_audit_campaign_only_stat_count',
    )
  }

  /** 查询投放层级 Adset 统计；该接口带分页参数，统一返回 items + total。 */
  async function getAdManagerAdsetOnlyStats(
    params: AdReviewV2Api.AdManagerHierarchyStatsParams,
  ) {
    return requestDwQueryPage<
      AdReviewV2Api.AdManagerHierarchyStatsParams,
      AdReviewV2Api.AdManagerHierarchyStatsItem
    >(
      params,
      'adsGovernorV2/dwQuery/bestads_ad_audit_adset_only_stat',
      'adsGovernorV2/dwQuery/bestads_ad_audit_adset_only_stat_count',
    )
  }

  /** 查询广告违规统计户形汇总，统一返回列表数组，页面无需感知 body.data 是否已解包。 */
  async function getAdManagerAccountTypeStats(
    params: AdReviewV2Api.AdManagerAccountTypeStatsParams,
  ) {
    const response = await requestClient.post<
      | AdReviewV2Api.AdManagerAccountTypeStatsBodyResponse
      | AdReviewV2Api.AdManagerAccountTypeStatsResponse
    >(
      prefix('adsGovernorV2/dwQuery/bestads_ad_audit_account_type_stat'),
      params,
    )

    return normalizeDwQueryList(response)
  }

  /**
   * 查询拒登广告统计列表。
   * POST /adsGovernorV2/dwQuery/bestads_ad_audit_adset_disapproved_stat
   * POST /adsGovernorV2/dwQuery/bestads_ad_audit_adset_disapproved_stat_count
   *
   * 列表数据和分页总数分别来自列表接口与 count 接口。
   */
  async function getDisapprovalStatsDay(
    params: AdReviewV2Api.DisapprovalStatsDayParams,
  ): Promise<AdReviewV2Api.DisapprovalStatsDayResponse> {
    return requestDwQueryPage(
      params,
      'adsGovernorV2/dwQuery/bestads_ad_audit_adset_disapproved_stat',
      'adsGovernorV2/dwQuery/bestads_ad_audit_adset_disapproved_stat_count',
    )
  }

  /** 拒登对比趋势可能返回完整 body，也可能已被请求客户端解包为点位数组。 */
  const normalizeDisapprovalStatsCompareTrend = (
    response:
      | AdReviewV2Api.DisapprovalStatsCompareTrendBodyResponse
      | AdReviewV2Api.DisapprovalStatsCompareTrendResult,
  ): AdReviewV2Api.DisapprovalStatsCompareTrendResult => {
    if (Array.isArray(response)) return response

    if (
      response &&
      typeof response === 'object' &&
      Array.isArray(response.data)
    ) {
      return response.data
    }

    return []
  }

  /**
   * 查询拒登广告对比趋势。
   * POST /adsGovernorV2/dwQuery/bestads_ad_audit_adset_disapproved_compare_trend
   */
  async function getDisapprovalStatsCompareTrend(
    params: AdReviewV2Api.DisapprovalStatsCompareTrendParams,
  ) {
    const response = await requestClient.post<
      | AdReviewV2Api.DisapprovalStatsCompareTrendBodyResponse
      | AdReviewV2Api.DisapprovalStatsCompareTrendResponse
    >(
      prefix(
        'adsGovernorV2/dwQuery/bestads_ad_audit_adset_disapproved_compare_trend',
      ),
      params,
    )

    return normalizeDisapprovalStatsCompareTrend(response)
  }

  /** 查询广告主详情按日趋势，统一返回列表数组，图表无需感知 body.data 是否已解包。 */
  async function getAdManagerCompanyStatsTrendDay(
    params: AdReviewV2Api.AdManagerCompanyStatsTrendDayParams,
  ) {
    const response = await requestClient.post<
      | AdReviewV2Api.AdManagerCompanyStatsTrendDayBodyResponse
      | AdReviewV2Api.AdManagerCompanyStatsTrendDayResponse
    >(
      prefix('adsGovernorV2/dwQuery/bestads_ad_audit_company_stat_trend_day'),
      params,
    )

    return normalizeDwQueryList(response)
  }

  /** 查询广告主详情违规标签 Top，统一返回列表数组，图表无需感知 body.data 是否已解包。 */
  async function getAdManagerNoneCompliantTop(
    params: AdReviewV2Api.AdManagerNoneCompliantTopParams,
  ) {
    const response = await requestClient.post<
      | AdReviewV2Api.AdManagerNoneCompliantTopBodyResponse
      | AdReviewV2Api.AdManagerNoneCompliantTopResponse
    >(
      prefix('adsGovernorV2/dwQuery/bestads_ad_audit_none_compliant_top'),
      params,
    )

    return normalizeDwQueryList(response)
  }

  /** 查询广告主详情高频违规词，接口与违规标签 Top 分离，避免 company_id 参数误传。 */
  async function getAdManagerViolationKeyPhrases(
    params: AdReviewV2Api.AdManagerViolationKeyPhrasesParams,
  ) {
    const response = await requestClient.post<
      | AdReviewV2Api.AdManagerViolationKeyPhrasesBodyResponse
      | AdReviewV2Api.AdManagerViolationKeyPhrasesResponse
    >(
      prefix('adsGovernorV2/dwQuery/bestads_ad_audit_violation_key_phrases'),
      params,
    )

    return normalizeDwQueryList(response)
  }

  /** 查询 Campaign 下拉选项。 */
  function queryCampaign(params: AdReviewV2Api.QueryCampaignParams) {
    return requestClient.post<
      AdReviewV2Api.RemoteSearchPageResponse<AdReviewV2Api.CampaignOptionItem>
    >(prefix('adsGovernorV2/queryCampaign'), params)
  }

  /** 查询 Adset 下拉选项。 */
  function queryAdset(params: AdReviewV2Api.QueryAdsetParams) {
    return requestClient.post<
      AdReviewV2Api.RemoteSearchPageResponse<AdReviewV2Api.AdsetOptionItem>
    >(prefix('adsGovernorV2/queryAdset'), params)
  }

  /** 查询 Ad 下拉选项。 */
  function queryAd(params: AdReviewV2Api.QueryAdParams) {
    return requestClient.post<
      AdReviewV2Api.RemoteSearchPageResponse<AdReviewV2Api.AdOptionItem>
    >(prefix('adsGovernorV2/queryAd'), params)
  }

  /** 查询广告账户下拉选项。 */
  function queryAdAccount(params: AdReviewV2Api.QueryAdAccountParams) {
    return requestClient.post<
      AdReviewV2Api.RemoteSearchPageResponse<AdReviewV2Api.AdAccountOptionItem>
    >(prefix('adsGovernorV2/queryAdAccount'), params)
  }

  /** 查询广告主下拉选项。 */
  function queryCompany(params: AdReviewV2Api.QueryCompanyParams) {
    return requestClient.post<
      AdReviewV2Api.RemoteSearchPageResponse<AdReviewV2Api.CompanyOptionItem>
    >(prefix('adsGovernorV2/queryCompany'), params)
  }

  return {
    approveMaterial,
    exportAdAuditList,
    getAdAuditDetail,
    getAdInsight,
    getAdManagerAccountOnlyStats,
    getAdManagerAccountTypeStats,
    getAdManagerAdsetOnlyStats,
    getAdManagerCampaignOnlyStats,
    getAdManagerCompanyStats,
    getAdManagerCompanyStatsTrendDay,
    getDisapprovalStatsCompareTrend,
    getDisapprovalStatsDay,
    getAdManagerNoneCompliantTop,
    getAdManagerViolationKeyPhrases,
    getLockedAdAuditList,
    getUnlockedAdAuditList,
    queryAd,
    queryAdAccount,
    queryAdset,
    queryCampaign,
    queryCompany,
    pollRemediationSuggestion,
    startRemediationSuggestion,
    startRetryTimedOut,
    tryLockAdAudit,
    unlockAdAudit,
  }
})
